import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVaultQuestionById,
  createVaultQuestionThunk,
  updateVaultQuestionThunk,
} from "../../redux/vaultQuestions/vaultQuestionThunks";
import { Icon } from "@iconify/react/dist/iconify.js";
import { AskedByBlock } from "./AskedBy";
import { isComplete, lockedBanner, saveErrorWords } from "./memberQuestionUtils";

const AddVaultQuestionsPageNew = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, questionData, error } = useSelector(
    (state) => state.vaultQuestion
  );

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState([]);
  const [selectedTier, setSelectedTier] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [selectedTags, setSelectedTags] = useState("");
  // "Say more" follow-up (2026-09-08): the line a member speaks to when
  // they add a voice note on this question. Deeper Dive only.
  const [sayMorePrompt, setSayMorePrompt] = useState("");

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (id) {
      dispatch(fetchVaultQuestionById({ id, navigate }));
    }
  }, [dispatch, id, navigate]);

  useEffect(() => {}, [questionData]);

  // A member question already shown to its city is Complete (founder
  // 2026-11-04, memberQuestionUtils SHOWN ONCE): its question, answers, tier
  // and type stay as members answered them, because its numbers are stored
  // by answer position. The backend refuses a change to any of the four
  // (409); tags still save. Only for the question this page loaded.
  const shownQuestion = isEditMode && questionData && String(questionData._id) === String(id) ? questionData : null;
  const locked = isComplete(shownQuestion);

  useEffect(() => {
    if (questionData && isEditMode) {
      setQuestion(questionData.questionText || "");
      setAnswer(questionData.answers || []);
      setSelectedTier(questionData?.tier || "");
      setQuestionType(questionData?.questionType);
      setSayMorePrompt(questionData?.sayMorePrompt || "");
      setSelectedTags(
        Array.isArray(questionData?.tags)
          ? questionData.tags.join(",")
          : questionData?.tags || ""
      );
    }
  }, [questionData, isEditMode]);

  useEffect(() => {
    // Ensure at least one answer input field exists
    if (answer.length === 0) {
      setAnswer([""]);
    }
  }, [answer]);

  // Function to clear messages
  const clearMessage = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };

  // Validate inputs

  const validateInputs = () => {
    const newErrors = {};
    const newAnswerErrors = [];

    // ✅ Validate Question (a locked question is sent as stored, unchecked)
    if (locked) {
      // nothing to check
    } else if (!question.trim()) {
      newErrors.question = "Question is required";
    } else {
      if (!/[A-Za-z]/.test(question)) {
        newErrors.question =
          "Question can not be only numbers or special characters";
      } else if (question.trim().length < 3) {
        newErrors.question = "Question must be at least 3 characters long";
      }
    }

    // ✅ Validate Type/Tier/Tags
    if (!selectedTier) {
      newErrors.tier = "Tier is required";
    }

    if (!questionType) {
      newErrors.questionType = "question Type is required";
    }

    if (!selectedTags.trim()) {
      newErrors.tags = "At least one tag is required";
    }
    // ✅ Validate Answers
    // ✅ Validate Answers only for multiple choice
    if (questionType === "multiple_choice" && !locked) {
      if (!answer.length || answer.every((ans) => ans.trim() === "")) {
        newErrors.answer = "At least one valid answer is required";
      }

      answer.forEach((ans, index) => {
        if (ans.trim() === "") {
          newAnswerErrors[index] = "Answer can not be empty";
        } else if (ans.trim().length < 5 || !/[A-Za-z]/.test(ans)) {
          newAnswerErrors[index] =
            "Answer must be at least 5 characters and contain letters.";
        } else {
          newAnswerErrors[index] = ""; // valid
        }
      });
    }

    // ✅ Set both errors
    setErrors({ ...newErrors, answerFieldErrors: newAnswerErrors });

    // Return if valid or not
    return (
      Object.keys(newErrors).length === 0 && newAnswerErrors.every((e) => !e)
    );
  };

  const handleAnswerChange = (index, value) => {
    const updated = [...answer];
    updated[index] = value;
    setAnswer(updated);

    const newAnswerErrors = [...(errors.answerFieldErrors || [])];
    if (value.trim() === "") {
      newAnswerErrors[index] = "Answer can not be empty";
    } else if (value.trim().length < 5 || !/[A-Za-z]/.test(value)) {
      newAnswerErrors[index] =
        "Answer must be at least 5 characters and contain letters.";
    } else {
      newAnswerErrors[index] = "";
    }

    const hasValid = updated.some(
      (ans) => ans.trim().length >= 5 && /[A-Za-z]/.test(ans)
    );

    setErrors((prev) => ({
      ...prev,
      answer: hasValid ? "" : prev.answer, // clear general error only if valid answer exists
      answerFieldErrors: newAnswerErrors,
    }));
  };

  const addAnswerField = () => {
    if (answer.length === 0) {
      setAnswer([""]);
      setErrors((prev) => ({
        ...prev,
        answerFieldErrors: [""], // no error until user interacts
        answer: "At least one valid answer is required",
      }));
      return;
    }

    const lastIndex = answer.length - 1;
    const lastAnswer = answer[lastIndex];

    if (!lastAnswer || lastAnswer.trim() === "") {
      // show error only on last empty field
      const updatedErrors = [...(errors.answerFieldErrors || [])];
      updatedErrors[lastIndex] = "Answer can not be empty";

      setErrors((prev) => ({
        ...prev,
        answerFieldErrors: updatedErrors,
      }));
      return;
    }

    // ✅ Add new empty answer without marking error immediately
    const newAnswers = [...answer, ""];
    setAnswer(newAnswers);

    // keep previous errors aligned, add empty string for new field
    const newAnswerErrors = [...(errors.answerFieldErrors || []), ""];

    const hasValid = newAnswers.some(
      (ans) => ans.trim().length >= 5 && /[A-Za-z]/.test(ans)
    );

    setErrors((prev) => ({
      ...prev,
      answerFieldErrors: newAnswerErrors,
      answer: hasValid ? "" : "At least one valid answer is required",
    }));
  };

  const removeAnswerField = (index) => {
    const updated = answer.filter((_, i) => i !== index);
    setAnswer(updated);

    // Keep errors aligned with answers
    const updatedErrors = [...(errors.answerFieldErrors || [])];
    updatedErrors.splice(index, 1);

    const hasValid = updated.some(
      (ans) => ans.trim().length >= 5 && /[A-Za-z]/.test(ans)
    );

    setErrors((prev) => ({
      ...prev,
      answerFieldErrors: updatedErrors,
      // global error should reset if at least one valid answer exists
      answer: hasValid ? "" : "At least one valid answer is required",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateInputs()) return;

    // A locked question sends its four locked fields exactly as stored, so a
    // tags-only save is not a change to them (backend lockedChanges).
    const vaultQuestionData = {
      questionText: locked ? shownQuestion.questionText : question,
      answers: locked ? shownQuestion.answers : answer,
      tier: locked ? shownQuestion.tier : selectedTier,
      questionType: locked ? shownQuestion.questionType : questionType,
      sayMorePrompt: selectedTier === "deeperDive" ? sayMorePrompt.trim() : "",
      tags: selectedTags
        .split(/[;,]/)
        .map((t) => t.trim())
        .filter(Boolean), // array for backend
    };

    try {
      let result;
      if (isEditMode) {
        result = await dispatch(
          updateVaultQuestionThunk({ id, data: vaultQuestionData, navigate })
        ).unwrap();
      } else {
        result = await dispatch(
          createVaultQuestionThunk({ data: vaultQuestionData, navigate })
        ).unwrap();
      }

      setSuccessMessage(
        result?.message || (isEditMode ? "Question updated" : "Question added")
      );

      clearMessage();
      setTimeout(() => navigate("/vault-questions"), 500);
    } catch (error) {
      console.error("Submit Error:", error);
      // The thunk rejects with the server's words (a string), such as a
      // shown member question's refusal (2026-11-04). Axios's own "Request
      // failed with status code 500" still reads "Something went wrong"
      // (review 2026-11-04), as before.
      setErrorMessage(saveErrorWords(error));
      clearMessage();
    }
  };

  // Helper: generate a pastel-ish color from a string
  const getTagColor = (tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`; // pastel colors
  };

  // Show loader when loading
  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header">
            {/* Who asked a member question, as on the View page
                (2026-11-04): the pencil shows it too. */}
            {shownQuestion && shownQuestion.askedBy ? (
              <div className="row">
                <AskedByBlock item={shownQuestion} />
              </div>
            ) : null}
          </div>
          <div className="card-body">
            {locked ? (
              <div
                className="bg-lilac-100 text-lilac-600 border border-lilac-600 radius-8 px-16 py-12 text-sm fw-medium mb-16"
                role="note"
              >
                {lockedBanner(shownQuestion)}
              </div>
            ) : null}
            {successMessage && (
              <SuccessAlert
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {errorMessage && (
              <DengerAlert
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}
            <form
              className="row gy-3 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              <div className="col-md-6 mt-3">
                <label className="form-label">
                  Tier<span className="text-danger-600">*</span>
                </label>
                <select
                  className={`form-select ${errors.tier ? "is-invalid" : ""}`}
                  id="tier"
                  value={selectedTier}
                  disabled={locked}
                  onChange={(e) => {
                    setSelectedTier(e.target.value);
                    setErrors((prev) => ({ ...prev, tier: "" }));
                  }}
                >
                  <option value="" disabled>
                    Select Tier
                  </option>
                  <option value="iceBreaker">iceBreaker</option>
                  <option value="deeperDive">deeperDive</option>
                  <option value="wildCard">wildCard</option>
                  <option value="dailyQuestion">Daily Questions</option>
                  <option value="keptTogether">Kept, Together</option>
                <option value="gatekeeper">gatekeeper</option>
                </select>
                {errors.tier && (
                  <p className="text-danger mt-1">{errors.tier}</p>
                )}
              </div>

              <div className="col-md-6 mt-3">
                <label className="form-label">
                  Question Type<span className="text-danger-600">*</span>
                </label>
                <select
                  className={`form-select ${
                    errors.questionType ? "is-invalid" : ""
                  }`}
                  id="questionType"
                  value={questionType}
                  disabled={locked}
                  onChange={(e) => {
                    setQuestionType(e.target.value);
                    setErrors((prev) => ({ ...prev, questionType: "" }));
                  }}
                >
                  <option value="" disabled>
                    Select Question Type
                  </option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="free_text">Free Text</option>
                </select>
                {errors.questionType && (
                  <p className="text-danger mt-1">{errors.questionType}</p>
                )}
              </div>
              {/* Tags (tag input with colors) */}
              <div className="col-md-12 mb-3">
                <label htmlFor="tags" className="form-label">
                  Tags<span className="text-danger-600">*</span>
                </label>
                <div
                  className={`d-flex flex-wrap align-items-center form-control tags-container ${
                    errors.tags ? "is-invalid" : ""
                  }`}
                  style={{ gap: "6px", minHeight: "38px", appearance: "none" }}
                  onClick={(e) =>
                    e.currentTarget.querySelector("input").focus()
                  }
                >
                  {(selectedTags
                    ? selectedTags
                        .split(/[;,]/)
                        .map((t) => t.trim())
                        .filter(Boolean)
                    : []
                  ).map((tag, tagIdx) => (
                    <span
                      key={tagIdx}
                      className="badge d-flex align-items-center"
                      style={{
                        gap: "4px",
                        backgroundColor: getTagColor(tag),
                        color: "#333", // dark text for contrast
                      }}
                    >
                      {tag}
                      <Icon
                        icon="ic:baseline-close"
                        className="cursor-pointer"
                        onClick={() => {
                          const newTags = selectedTags
                            ? selectedTags
                                .split(/[;,]/)
                                .map((t) => t.trim())
                                .filter(Boolean)
                            : [];
                          newTags.splice(tagIdx, 1);
                          setSelectedTags(newTags.join(",")); // ✅ Update state directly
                        }}
                      />
                    </span>
                  ))}

                  <input
                    type="text"
                    className="border-0 flex-grow-1"
                    style={{ outline: "none", minWidth: "100px" }}
                    placeholder="Type and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        const newTag = e.target.value.trim();
                        if (newTag) {
                          const newTags = selectedTags
                            ? selectedTags
                                .split(/[;,]/)
                                .map((t) => t.trim())
                                .filter(Boolean)
                            : [];
                          if (!newTags.includes(newTag)) {
                            newTags.push(newTag);
                            setSelectedTags(newTags.join(",")); // ✅ Update state directly
                          }
                          if (errors.tags) {
                            setErrors((prev) => ({ ...prev, tags: "" }));
                          }
                        }
                        e.target.value = "";
                      } else if (e.key === "Backspace" && !e.target.value) {
                        // Remove last tag if input is empty
                        const newTags = selectedTags
                          ? selectedTags
                              .split(/[;,]/)
                              .map((t) => t.trim())
                              .filter(Boolean)
                          : [];
                        if (newTags.length > 0) {
                          newTags.pop();
                          setSelectedTags(newTags.join(",")); // ✅ update state directly
                        }
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                        .trimStart(); // Prevent leading spaces
                    }}
                  />
                </div>
                {errors.tags && (
                  <p className="text-danger mt-1">{errors.tags}</p>
                )}
              </div>
              <div className="col-md-12">
                <label className="form-label">
                  Question<span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.question ? "is-invalid" : ""
                  }`}
                  id="question"
                  placeholder="Enter question"
                  value={question}
                  disabled={locked}
                  maxLength={64}
                  onChange={(e) => {
                    setQuestion(e.target.value);
                    setErrors((prev) => ({ ...prev, question: "" }));
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                      .trimStart(); // Prevent leading spaces
                  }}
                />
                {errors.question && (
                  <p className="text-danger mt-1">{errors.question}</p>
                )}
              </div>

              {selectedTier === "deeperDive" && (
                <div className="col-md-12">
                  <label className="form-label">Say more follow-up</label>
                  <input
                    type="text"
                    className="form-control"
                    id="sayMorePrompt"
                    placeholder="e.g. What changed it?"
                    value={sayMorePrompt}
                    maxLength={80}
                    onChange={(e) => setSayMorePrompt(e.target.value)}
                  />
                  <p className="text-secondary-light mt-1 mb-0" style={{ fontSize: 12 }}>
                    Shown under the Say more chip on this Deeper Dive question. The member speaks to this line, not to the multiple choice. Leave empty for the plain chip.
                  </p>
                </div>
              )}

              {questionType === "multiple_choice" && (
                <div className="col-md-12">
                  <label className="form-label">
                    Answer<span className="text-danger-600">*</span>
                  </label>
                  {answer.map((answerItem, index) => (
                    <div key={index} className="mb-10">
                      <div className="d-flex align-items-center mb-2">
                        <input
                          type="text"
                          className={`form-control me-3 ${
                            errors?.answerFieldErrors?.[index]
                              ? "is-invalid"
                              : ""
                          }`}
                          // maxLength={64}
                          placeholder={`Answer ${index + 1}`}
                          value={answerItem}
                          disabled={locked}
                          onChange={(e) =>
                            handleAnswerChange(index, e.target.value)
                          }
                          onInput={(e) => {
                            e.target.value = e.target.value
                              .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                              .trimStart(); // Prevent leading spaces
                          }}
                        />

                        {answer.length > 1 && !locked && (
                          <button
                            type="button"
                            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                            onClick={() => removeAnswerField(index)}
                          >
                            <Icon
                              icon="ic:baseline-minus"
                              className="icon text-xl line-height-1"
                            />
                          </button>
                        )}
                      </div>
                      {errors?.answerFieldErrors?.[index] && (
                        <p className="text-danger mb-1">
                          {errors.answerFieldErrors[index]}
                        </p>
                      )}
                    </div>
                  ))}

                  {errors.answer && (
                    <p className="text-danger mt-1">{errors.answer}</p>
                  )}
                  {!locked && (
                    <button
                      type="button"
                      className="btn btn-primary text-sm btn-sm px-12 py-12 mt-10 radius-8 d-flex align-items-center gap-2"
                      onClick={addAnswerField}
                    >
                      <Icon
                        icon="ic:baseline-plus"
                        className="icon text-xl line-height-1"
                      />
                      Add New
                    </button>
                  )}
                </div>
              )}
              <div className="col-12">
                <button
                  className="btn btn-primary mt-3 float-end"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVaultQuestionsPageNew;
